<template>
  <TransitionWrapper
    name="fade"
  >
    <ModalStructure
      class="modalStyles"
      hasBlur
      :shouldCloseOnBlur="false"
      :showModal="showSelectionModal"
      showCloseButton
      @close="handleClose"
    >
      <template v-slot:header>
        <div class="icon-title__container">
          <ApplicationAvatar />
          <div class="title-desc__container">
            <HeadingLarge
              class="modal__heading"
              :data-test-id="$testId('modalHeadingText')"
              :hasMargin="false"
            >
              Import New Users from {{ applicationDisplayName }}
            </HeadingLarge>
            <ParagraphBody :data-test-id="$testId('headingSubtext')">
              {{ headingSubText }}
            </ParagraphBody>
          </div>
        </div>
      </template>
      <template v-slot:body>
        <div v-if="customApiFeatureFlag">
          <ImportUsersPaywallMessage
            v-if="isFreeUserOverLimit"
            class="body__paywall"
            :userLimitMessage="maxFreeUsersLimitMessage"
          />
          <UserCapBanner
            v-else-if="isProviderOrg"
            altLicenseText
            class="body__user-cap-banner"
            :totalCountOfAllItems="usersCountNew"
          />
        </div>
        <div class="body__wrapper">
          <HeadingLarge
            v-if="modalState === $options.ModalStateEnum.NO_USERS_STATE"
            class="no-users__text"
            :data-test-id="$testId('noUsersText')"
          >
            <strong>No Users Returned</strong>
          </HeadingLarge>
          <ItemListCollectionWrapper
            v-else-if="modalState === $options.ModalStateEnum.SELECTION_STATE"
            :collectionModule="$options.ScimUsersImportCollection"
            defaultSortBy="email"
            rowsPerPageLocalStorageKey=""
            selectable
            @selectAll="handleSelectAll"
            @selectItem="handleSelect"
          >
            <template v-slot="slotProps">
              <ItemListPagination
                :columns="itemListColumns"
                :currentPageNum="slotProps.currentPageNum"
                :indexToAddLine="slotProps.indexToAddLine"
                :isLoading="slotProps.loading"
                :rowsPerPage="slotProps.rowsPerPage"
                :selectAll="slotProps.selectAll"
                :selectable="slotProps.selectable"
                :showEmptyState="slotProps.showEmptyState"
                :sortBy="slotProps.sortBy"
                :sortDirection="slotProps.sortDirection"
                :tableData="slotProps.tableData"
                :totalCount="slotProps.totalCount"
                @pageChange="slotProps.handlePageChange"
                @select="slotProps.handleSelect"
                @selectAll="slotProps.handleSelectAll"
                @sortColumn="slotProps.handleSortColumn"
              >
                <template #importStatus="{ record }">
                  <ImportStatus
                    field="isNew"
                    :item="record"
                  />
                </template>
                <template #lastname="{ lastname }">
                  <ParagraphBody>{{ lastname }}</ParagraphBody>
                </template>
                <template #firstname="{ firstname }">
                  <ParagraphBody>{{ firstname }}</ParagraphBody>
                </template>
                <template #email="{ email }">
                  <ParagraphBody>{{ email }}</ParagraphBody>
                </template>
                <template #creationSource="{ creationSource }">
                  <ParagraphBody>{{ creationSource }}</ParagraphBody>
                </template>
                <template #userStatusInSourceProvider="{ userStatusInSourceProvider }">
                  <ParagraphBody>{{ userStatusInSourceProvider }}</ParagraphBody>
                </template>
              </ItemListPagination>
            </template>
          </ItemListCollectionWrapper>

          <!--
            TODO: Phase out the "user-by-user" results view with bulk import
            results view (add a prop for mandatory bulk import view?)
          -->
          <ImportUsersResults
            v-else
            :importType="`scim:${item.displayName}`"
            :itemType="itemType"
          />
        </div>
      </template>
      <template v-slot:footer>
        <div class="footer__container">
          <div class="divider" />
          <template v-if="!showResultsView">
            <div class="user-counts__container">
              <ParagraphBody :data-test-id="$testId('countTotalUsersReturned')">
                <b>Total Users Returned: </b>{{ allProviderUsersTotalCount }}
              </ParagraphBody>
              <ParagraphBody :data-test-id="$testId('countCurrentlyInJumpCloud')">
                <b>Currently in JumpCloud: </b>{{ totalSystemUsers }}
              </ParagraphBody>
              <ParagraphBody :data-test-id="$testId('countToBeImported')">
                <b>To be Imported: </b>{{ usersCountNew }}
              </ParagraphBody>
            </div>
            <div class="divider" />
          </template>
          <div class="buttons__container">
            <ButtonGroup
              class="button-group"
              :isFullWidth="false"
            >
              <Button
                v-if="!showResultsView"
                :data-test-id="$testId('secondaryButton')"
                hasOutline
                text="Cancel"
                type="secondary"
                @click="handleClose"
              />
              <Button
                :data-test-id="$testId('primaryButton')"
                :isDisabled="disableImportButton"
                :isLoading="loadingOverlay"
                :text="primaryButtonText"
                textCase="inherit"
                type="primary"
                @click="handleContinue"
              />
            </ButtonGroup>
          </div>
        </div>
      </template>
    </ModalStructure>
  </TransitionWrapper>
</template>

<script>
import {
  Button,
  ButtonGroup,
  HeadingLarge,
  ItemListPagination,
  ModalStructure,
  ParagraphBody,
  TransitionWrapper,
} from '@jumpcloud/ui-components';
import {
  getApplicationDisplayName,
  getApplicationImportSourceName,
  getItemListColumns,
} from 'client/applications/utils/ApplicationUtils';
import { mapActions, mapGetters, mapState } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';

import { ItemTypeConsts } from 'client/util/Constants';
import { PERSONIO, scimConnectionTypes } from 'client/util/Constants/ApplicationConstants';
import ApplicationAvatar from 'client/applications/ApplicationAvatar.vue';
import CloudDirectoryImportMixin from 'client/directories/mixins/CloudDirectoryImportMixin';
import ImportStatus from 'client/components/ItemTable/BodyComponents/ImportStatus.vue';
import ImportUsersConstants from 'client/util/Constants/ImportUsersConstants';
import ImportUsersPaywallMessage from 'client/components/ImportUsers/ImportUsersPaywallMessage';
import ImportUsersResults from 'client/components/ImportUsers/ImportUsersResults.vue';
import ItemListCollectionWrapper from 'client/components/ItemList/ItemListCollectionWrapper.vue';
import UserCapBanner from 'client/Payments/components/UserCapBanner';
import Util from 'client/util/Util';

const ApplicationModel = 'ApplicationModel';

const SettingsModule = 'SettingsModule';
const ScimUsersImportCollection = 'ScimUsersImportCollection';
const OrganizationsCollection = 'OrganizationsCollection';

const ModalStateEnum = {
  NO_USERS_STATE: 0,
  SELECTION_STATE: 1,
  RESULTS_STATE: 2,
};

const {
  selectionHeaderSubtext,
  resultsHeaderSubtext,
} = ImportUsersConstants.scimUsersImportSubHeaderTexts;

export default {
  name: 'MigratedScimUsersImport',

  components: {
    ApplicationAvatar,
    Button,
    ButtonGroup,
    HeadingLarge,
    ImportStatus,
    ImportUsersResults,
    ImportUsersPaywallMessage,
    ItemListPagination,
    ItemListCollectionWrapper,
    ModalStructure,
    ParagraphBody,
    TransitionWrapper,
    UserCapBanner,
  },

  mixins: [CloudDirectoryImportMixin],

  props: {
    scimAppId: {
      type: String,
      required: true,
    },
    showSelectionModal: {
      type: Boolean,
      required: true,
    },
  },

  data: () => ({
    applicationDisplayName: '',
    loadingPageChange: false,
    loadingOverlay: false,
    itemType: ItemTypeConsts.scimUserImport,
    noUsersReturnedState: false,
    rowsPerPage: 10,
    showResultsView: false,
    totalSystemUsers: 0,
    usersCountNew: 0,
    usersCountUpdate: 0,
  }),

  ModalStateEnum,
  ScimUsersImportCollection,

  computed: {
    ...mapState(ApplicationModel, ['item', 'scimConnectionType']),

    ...mapState(ScimUsersImportCollection, {
      boundIds: state => state.entities.boundItems.byId,
    }),

    ...mapState(SettingsModule, [
      'isCustomer',
      'isProviderOrg',
      'maxSystemUsers',
      'orgId',
    ]),

    ...mapGetters(OrganizationsCollection, ['getItemDetails']),

    ...mapGetters(ScimUsersImportCollection, {
      accumulatedProviderUsers: 'getCollection',
      allProviderUsersTotalCount: 'getTotalCount',
      getIdmUser: 'getItem',
    }),

    customApiFeatureFlag() {
      return this.$hasFeatureFlag(this.$featureFlags.importUserUpdatesCustomApi);
    },

    disableImportButton() {
      const noUsersSelected = this.usersCountNew === 0 && this.usersCountUpdate === 0;

      return this.loadingOverlay || noUsersSelected;
    },

    headingSubText() {
      return !this.showResultsView ? selectionHeaderSubtext : resultsHeaderSubtext;
    },

    isFreeUserOverLimit() {
      return !this.isCustomer && this.usersCountNew > this.maxSystemUsers;
    },

    itemListColumns() {
      return getItemListColumns(this.item.displayName);
    },

    maxFreeUsersLimitMessage() {
      return `Importing users may put you over the ${this.maxSystemUsers} user limit on your org and cause the import to fail.
To grow your account beyond ${this.maxSystemUsers} users, select one of our plans.`;
    },

    modalState() {
      let modalState;

      if (!this.accumulatedProviderUsers?.length) {
        modalState = ModalStateEnum.NO_USERS_STATE;
      } else if (this.accumulatedProviderUsers.length && !this.showResultsView) {
        modalState = ModalStateEnum.SELECTION_STATE;
      } else {
        modalState = ModalStateEnum.RESULTS_STATE;
      }

      return modalState;
    },

    primaryButtonText() {
      return this.modalState !== ModalStateEnum.RESULTS_STATE
        ? 'Import'
        : 'Go to Users Page';
    },
  },

  async created() {
    /**
     * item.name -> internal name. what kind of integration it is
     * item.displayName -> name displayed below icon in aside
     * item.displayLabel -> user-given label: used on General Info tab
     */
    this.applicationDisplayName = getApplicationDisplayName(this.item);

    await this.getDetailsForOrganization({ id: this.orgId });
    this.totalSystemUsers = this.getItemDetails(this.orgId)?.systemUsersCount || 0;

    if (this.accumulatedProviderUsers?.length) {
      const scimAppSourceName = getApplicationImportSourceName(
        this.item,
        this.scimConnectionType,
      );

      const appConnectionType = this.item.displayName === PERSONIO
        ? scimConnectionTypes.CUSTOM
        : this.scimConnectionType;

      this.setScimApplicationId({ scimAppId: this.$props.scimAppId });
      this.setUsersFilterField({ key: '', value: '' });
      this.setScimApplicationSourceName({ scimAppSourceName });
      this.setApplicationConnectionType({ appConnectionType });
      this.setPage({ pageNumber: this.currentPageNum, limit: this.rowsPerPage });
    }
  },

  destroyed() {
    this.resetCollection(true);
  },

  methods: {
    ...mapActions(ScimUsersImportCollection, [
      'addIsNewFieldToUsers',
      'fetchCollection',
      'importSelectedUsers',
      'resetCollection',
      'setApplicationConnectionType',
      'setPage',
      'setScimApplicationId',
      'setScimApplicationSourceName',
      'setUsersFilterField',
    ]),

    ...mapActions(OrganizationsCollection, ['getDetailsForOrganization']),

    ...mapActions(notificationModule, ['triggerErrorNotification']),

    ...mapGetters(ScimUsersImportCollection, [
      'getJobId',
    ]),

    handleClose() {
      this.resetCollection(true);
      this.$emit('close');
    },

    async handleContinue() {
      if (this.modalState !== ModalStateEnum.RESULTS_STATE) {
        this.loadingOverlay = true;
        try {
          await this.importSelectedUsers();
          this.showResultsView = true;
        } catch (e) {
          const errorMessage = e?.message || 'Please try again.';
          this.triggerErrorNotification({
            title: 'Error importing selected Users',
            message: `Unable to import selected Users from this provider: ${errorMessage}`,
          });
        } finally {
          this.loadingOverlay = false;
        }
        this.$emit('continue', this.getJobId());
      } else {
        Util.redirect('#/users');

        this.handleClose();
      }
    },

    /**
     * This method takes in a ItemList item (user) and selects/unselects them, meaning:
     * - add/remove them from the new/import counts at the bottom of the modal
     * @param {object} selectedUser ItemList item (user) that has been selected/unselected
     * @param {boolean} selectedEvalOverride decides whether to toggle selected status or leave same
     */
    handleSelect(selectedUser = {}) {
      let isNewUser = selectedUser.isNew;
      if (typeof isNewUser === 'undefined') {
        const scimUser = this.getIdmUser(selectedUser.itemId);
        isNewUser = scimUser ? scimUser.isNew : false;
      }

      if (selectedUser.checkedStatus === true) {
        if (isNewUser) this.usersCountNew += 1;
        else this.usersCountUpdate += 1;
      } else {
        // eslint-disable-next-line no-lonely-if
        if (isNewUser) this.usersCountNew -= 1;
        else this.usersCountUpdate -= 1;
      }
    },

    async handleSelectAll(selected) {
      // reset counters
      this.usersCountNew = 0;
      this.usersCountUpdate = 0;

      if (selected) {
        // set all users' selected status according to the current select all button's
        // selected status and call `handleSelect` on each user
        let users = [];
        Object.entries(this.boundIds).forEach(item => {
          const user = item[1];
          // eslint-disable-next-line no-param-reassign
          user.checkedStatus = selected;
          users.push(user);
        });

        users = await this.addIsNewFieldToUsers({ users, emailField: 'email' });

        users.forEach(user => {
          this.handleSelect(user);
        });
      }
    },
  },
};
</script>

<style scoped>
.body__wrapper {
  min-height: 500px;
}

.button-group {
  flex-basis: 100%;
  margin-top: 20px;
  user-select: none;
}

.divider {
  background-color: var(--jc-color-neutral-stroke);
  height: 2px;
  width: 100%;
}

.footer__container {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.icon-title__container {
  display: flex;
}

.modalStyles {
  --modal-width: 75%;
}

.modal__heading {
  margin-bottom: var(--jc-spacer-small);
}

.no-users__text {
  color: var(--jc-color-disabled-text);
  display: flex;
  justify-content: center;
  margin-top: var(--jc-spacer-medium);
}

.body__paywall {
  background-color: var(--jcInformational50);
  white-space: pre-wrap;
}

.provider__icon {
  height: 60px;
  margin-right: var(--jc-spacer-medium);
}

.title-desc__container {
  margin-left: 20px;
}

.user-counts__container {
  display: flex;
  margin-bottom: var(--jc-spacer-x-small);
  margin-top: 20px;
}

.user-counts__container p:not(:last-of-type) {
  margin-right: var(--jc-spacer);
}

.users-loading-icon {
  align-items: center;
  display: flex;
  justify-content: center;
}

.body__user-cap-banner {
  background-color: var(--jcInformational100);
}
</style>
